import { AxiosResponse } from 'axios'
import {
  ActivePrice,
  ActivePrices,
  StockAvailabilityType,
  getActivePrices,
  getStockAvailabilities,
} from '../products/storage'
import { typesenseInstantsearchAdapter } from './Client'
import {
  BrandResponse,
  CategoryResponse,
  SearchResponse,
  SearchResponseHit,
  TypesenseProduct,
} from './types'
import { generateSearchRequests } from './utils'
import { SelectedFiltersType } from '../products/components/ProductFilter'
import { merge } from 'lodash'

export const collection = 'products'

export interface MultiSearchParams {
  accountNumber: string | undefined
  currentPage: number
  selectedFilters: SelectedFiltersType
  sortBy: string
  branchId?: string
}

export async function multiSearchWithActivePrices({
  accountNumber,
  currentPage,
  selectedFilters,
  sortBy,
  branchId,
}: MultiSearchParams): Promise<{ searchResults: SearchResponse<TypesenseProduct & ActivePrice> }> {
  const { searchResults } = await multiSearch(currentPage, selectedFilters, sortBy)
  const ids = searchResults.hits?.map((e) => parseInt(e.document.id.toString(), 10)) ?? []

  const promises = []
  let productIdsWithStock: number[] = []

  if (searchResults.hits && ids.length > 0) {
    promises.push(getActivePrices(accountNumber, ids, branchId))
    productIdsWithStock =
      searchResults.hits
        ?.filter((hit) => !hit.document.has_variants)
        .map((hit) => parseInt(hit.document.id.toString(), 10)) ?? []

    if (productIdsWithStock.length > 0)
      promises.push(getStockAvailabilities({ productIds: productIdsWithStock }))

    const results = await Promise.all(promises)
    const activePrices = results[0] as AxiosResponse<ActivePrices> | undefined
    const stockAvailabilities = results[1] as StockAvailabilityType[] | undefined

    if (activePrices?.status.toString().startsWith('2')) {
      const data = activePrices.data

      searchResults.hits = searchResults.hits.map((e) => {
        const match = data.prices.find((p) => {
          return p.productId.toString() === e.document.id.toString()
        })

        let stockAvailabilitiesMatch: StockAvailabilityType[] | undefined = undefined
        if (stockAvailabilities) {
          stockAvailabilitiesMatch = stockAvailabilities?.filter((s) => {
            return s.productId.toString() === e.document.id.toString()
          })
        }

        let doc: TypesenseProduct & ActivePrice
        if (match) {
          doc = merge(e.document, match, { stockAvailabilities }) as TypesenseProduct & ActivePrice
        } else {
          doc = merge(
            e.document,
            {
              discountedPriceIncGst: 0,
              discountedPriceExcGst: 0,
              basePriceIncGst: 0,
              basePriceExcGst: 0,
            },
            { stockAvailabilities: stockAvailabilitiesMatch },
          ) as TypesenseProduct & ActivePrice
        }

        return {
          ...e,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          document: doc!,
        } as SearchResponseHit<TypesenseProduct & ActivePrice>
      })
    }
  }

  return { searchResults } as { searchResults: SearchResponse<TypesenseProduct & ActivePrice> }
}

export async function multiSearch(
  currentPage: number,
  selectedFilters: SelectedFiltersType,
  sortBy: string,
): Promise<{ searchResults: SearchResponse<TypesenseProduct & ActivePrice> }> {
  const searchRequests = generateSearchRequests(currentPage, selectedFilters, sortBy)

  const hits = await typesenseInstantsearchAdapter.typesenseClient.multiSearch.perform<
    (TypesenseProduct & ActivePrice)[]
  >(searchRequests, {})

  return {
    searchResults: hits.results[0],
  }
}

type Empty = {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
}

export async function getCategories() {
  const hits = await typesenseInstantsearchAdapter.typesenseClient.multiSearch.perform<Empty[]>(
    {
      searches: [
        {
          collection: collection,
          per_page: 0,
          facet_by: 'categories',
          max_facet_values: 1000,
          q: '*',
          query_by: 'name',
          sort_by: '_text_match:desc',
          exclude_fields: 'embedding',
        },
      ],
    },
    {},
    {
      cacheSearchResultsForSeconds: 3600,
    },
  )

  const categories =
    hits.results[0].facet_counts?.[0].counts.map(
      (e) =>
        ({
          count: e.count,
          value: e.value,
        }) as CategoryResponse,
    ) ?? []
  return {
    categories: [...categories].sort((a, b) => (a.value > b.value ? 1 : -1)),
  }
}

export async function getBrands() {
  const hits = await typesenseInstantsearchAdapter.typesenseClient.multiSearch.perform<Empty[]>(
    {
      searches: [
        {
          collection: collection,
          per_page: 0,
          facet_by: 'brand',
          max_facet_values: 1000,
          q: '*',
          query_by: 'name',
          sort_by: '_text_match:desc',
          exclude_fields: 'embedding',
        },
      ],
    },
    {},
    {
      cacheSearchResultsForSeconds: 3600,
    },
  )

  const brands =
    hits.results[0].facet_counts?.[0].counts.map(
      (e) =>
        ({
          count: e.count,
          value: e.value,
        }) as BrandResponse,
    ) ?? []
  return {
    brands: brands,
  }
}

export async function autoSearch(searchInput: string) {
  if (!searchInput) {
    return {} as SearchResponse<TypesenseProduct>
  }

  const hits = await typesenseInstantsearchAdapter.typesenseClient.multiSearch.perform<
    TypesenseProduct[]
  >({
    searches: [
      {
        collection: collection,
        per_page: 4,
        page: 1,
        q: searchInput,
        query_by: 'name,item_id,online_description,brand,embedding,l1_category,l2_category',
        exclude_fields: 'embedding',
      },
    ],
  })

  return hits.results[0]
}

export async function getProductByMasterProductId(
  masterId: number,
): Promise<TypesenseProduct | undefined> {
  const hits = await typesenseInstantsearchAdapter.typesenseClient.multiSearch.perform<
    TypesenseProduct[]
  >({
    searches: [
      {
        collection: collection,
        filter_by: `id:=${masterId}`,
        q: '*',
        query_by: 'name',
        exclude_fields: 'embedding',
      },
    ],
  })

  let product: TypesenseProduct | undefined
  if (hits.results[0].hits && hits.results[0].hits?.length > 0) {
    product = hits.results[0].hits[0].document
  }

  return product
}
